import { useEffect, useRef, useState } from 'react';
import './App.css';

const publicPath = process.env.PUBLIC_URL || '';
const getAssetPath = (assetPath) => `${publicPath}${assetPath}`;
const getImageRoute = (slug) => `${publicPath}/image/${slug}`;

const navigation = [
  { label: 'Home', href: '#top' },
  { label: 'About', href: '#about' },
  { label: 'Collections', href: '#collections' },
];

const collections = [
  {
    title: 'Tufting studio',
    description: 'Classic floorpieces crafted with depth, pattern discipline, and heirloom warmth.',
    image: getAssetPath('/asset/Carpet Weaving/1.jpeg'),
  },
  {
    title: 'Hand Tufed',
    description: 'Lush textures and rich detailing for grand living spaces and hospitality interiors.',
    image: getAssetPath('/asset/Hand Tufted/1.jpeg'),
  },
  {
    title: 'Modern heritage',
    description: 'Fresh geometries and pared-back palettes for contemporary homes.',
    image: getAssetPath('/asset/HandTufted Modern/1.jpeg'),
  },
];

const craftsmanshipPoints = [
  {
    title: 'Legacy',
    text: 'A rich tradition of carpet-making refined over generations in Bhadohi.',
  },
  {
    title: 'Design',
    text: 'Every pattern is developed to feel tailored, balanced, and enduring.',
  },
  {
    title: 'Craft',
    text: 'From yarn selection to finishing, every stage is handled with care.',
  },
  {
    title: 'Luxury',
    text: 'The result is a carpet that elevates architecture, comfort, and character.',
  },
];

const showcaseGroups = [
  {
    title: 'Carpet weaving studio',
    description: 'Traditional weaving stories shaped through rich motifs and tactile depth.',
    items: [
      { title: 'Binding Process', slug: 'carpet-weaving-2', image: getAssetPath('/asset/Carpet Weaving/2.jpeg') },
      { title: 'Detailed Weaving', slug: 'carpet-weaving-1', image: getAssetPath('/asset/Carpet Weaving/3.jpeg') },
      
    ],
  },
  {
    title: 'Hand tufted elegance',
    description: 'Soft pile compositions made to feel inviting, luxurious, and refined.',
    items: [
      { title: 'Tufted Detail 1', slug: 'hand-tufted-2', image: getAssetPath('/asset/Hand Tufted/2.jpeg') },
      { title: 'Tufted Detail 2', slug: 'hand-tufted-3', image: getAssetPath('/asset/Hand Tufted/3.jpeg') },
      { title: 'Tufted Detail 3', slug: 'hand-tufted-4', image: getAssetPath('/asset/Hand Tufted/4.jpeg') },
    ],
  },
  {
    title: 'Modern hand-tufted forms',
    description: 'Clean, contemporary interpretations for hospitality and private residences.',
    items: [
      { title: 'Modern Detail 1', slug: 'modern-tufted-1', image: getAssetPath('/asset/HandTufted Modern/1.jpeg') },
      { title: 'Modern Detail 2', slug: 'modern-tufted-2', image: getAssetPath('/asset/HandTufted Modern/2.jpeg') },
      { title: 'Modern Detail 3', slug: 'modern-tufted-3', image: getAssetPath('/asset/HandTufted Modern/3.jpeg') },
    ],
  },
  {
    title: 'Handloom craftsmanship',
    description: 'Structured weaves and rich textures shaped by traditional Bhadohi makers.',
    items: [
      { title: 'Handloom Detail 1', slug: 'handloom-1', image: getAssetPath('/asset/Handloom/1.jpeg') },
      { title: 'Handloom Detail 2', slug: 'handloom-2', image: getAssetPath('/asset/Handloom/2.jpeg') },
      { title: 'Handloom Detail 3', slug: 'handloom-3', image: getAssetPath('/asset/Handloom/3.jpeg') },
      { title: 'Handloom Detail 4', slug: 'handloom-4', image: getAssetPath('/asset/Handloom/4.jpeg') },
      { title: 'Handloom Detail 5', slug: 'handloom-5', image: getAssetPath('/asset/Handloom/5.jpeg') },
      { title: 'Handloom Detail 6', slug: 'handloom-6', image: getAssetPath('/asset/Handloom/6.jpeg') },
      { title: 'Handloom Detail 7', slug: 'handloom-7', image: getAssetPath('/asset/Handloom/7.jpeg') },
    ],
  },
];

const mobileCarouselImages = [
  { src: getAssetPath('/asset/Handloom/1.jpeg'), alt: 'Handloom carpet detail' },
  { src: getAssetPath('/asset/Handloom/2.jpeg'), alt: 'Handloom texture detail' },
  { src: getAssetPath('/asset/Handloom/3.jpeg'), alt: 'Handloom weaving detail' },
  { src: getAssetPath('/asset/Handloom/4.jpeg'), alt: 'Handloom pattern detail' },
  { src: getAssetPath('/asset/Handloom/5.jpeg'), alt: 'Handloom close-up detail' },
  { src: getAssetPath('/asset/Hand Tufted/2.jpeg'), alt: 'Hand-tufted carpet detail' },
  { src: getAssetPath('/asset/Hand Tufted/3.jpeg'), alt: 'Hand-tufted texture detail' },
  { src: getAssetPath('/asset/HandTufted Modern/3.jpeg'), alt: 'Modern tufted carpet design' },
];

function App() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isAboutExpanded, setIsAboutExpanded] = useState(false);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 900);
  const [mobilePage, setMobilePage] = useState(null);
  const [activeCarouselIndex, setActiveCarouselIndex] = useState(0);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 900px)');
    const updateIsMobile = () => {
      const mobile = mediaQuery.matches;
      setIsMobile(mobile);
      if (!mobile) {
        setMobilePage(null);
      }
    };

    updateIsMobile();
    mediaQuery.addEventListener('change', updateIsMobile);

    return () => mediaQuery.removeEventListener('change', updateIsMobile);
  }, []);

  const handleMobilePage = (page) => {
    if (isMobile) {
      setMobilePage(page);
      setIsMenuOpen(false);
    }
  };

  const handleHomeView = () => {
    setMobilePage(null);
    setIsMenuOpen(false);
    setIsAboutExpanded(false);
    window.scrollTo({ top: 0, behavior: 'auto' });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  };

  const changeCarouselIndex = (direction) => {
    setActiveCarouselIndex((prev) => (prev + direction + mobileCarouselImages.length) % mobileCarouselImages.length);
  };

  const touchStartXRef = useRef(null);

  const handleCarouselTouchStart = (event) => {
    touchStartXRef.current = event.touches[0].clientX;
  };

  const handleCarouselTouchEnd = (event) => {
    if (touchStartXRef.current === null) {
      return;
    }

    const deltaX = event.changedTouches[0].clientX - touchStartXRef.current;
    const swipeThreshold = 40;

    if (deltaX > swipeThreshold) {
      changeCarouselIndex(-1);
    } else if (deltaX < -swipeThreshold) {
      changeCarouselIndex(1);
    }

    touchStartXRef.current = null;
  };

  useEffect(() => {
    if (!isMobile || !mobilePage) {
      return;
    }

    const intervalId = window.setInterval(() => {
      setActiveCarouselIndex((prev) => (prev + 1) % mobileCarouselImages.length);
    }, 6000);

    return () => window.clearInterval(intervalId);
  }, [isMobile, mobilePage]);

  useEffect(() => {
    if (mobilePage) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          } else {
            entry.target.classList.remove('visible');
          }
        });
      },
      {
        threshold: 0.2,
      }
    );

    document.querySelectorAll('.scroll-reveal').forEach((element) => {
      observer.observe(element);
    });

    return () => observer.disconnect();
  }, [mobilePage, isMobile]);

  const aboutSection = (
    <section id="about" className="section intro-section">

      <div className="intro-copy intro-copy-below scroll-reveal reveal-up">
        <p className="eyebrow">About Orovia Bhadohi Carpets</p>
        <p>
          <strong>Where Heritage Meets Timeless Craftsmanship</strong>
        </p>
        <p>
          Welcome to Orovia Bhadohi Carpets, a distinguished manufacturer of premium handmade and machine-made carpets, proudly based in Bhadohi, India—renowned worldwide as the Carpet City of India.
        </p>
        <p>
          Our story began in 1980, when our family embarked on a journey dedicated to the art of carpet weaving. Today, as a second-generation family business, we continue to uphold this legacy with the same passion, integrity, and commitment to quality that has defined us for over five decades.
        </p>
        <p>
          In 2026, Orovia Bhadohi Carpets embraced the digital era, expanding our reach to serve clients across the globe while remaining deeply connected to the craftsmanship that has been passed down through generations.
        </p>
        <p>
          We manufacture an extensive collection of handcrafted and machine-made floor coverings, including handmade wool carpets &amp; wool durries, cotton durries, handloom carpets &amp; durries, tencel carpets, viscose carpets, jute durries, hand-tufted carpets, and premium machine-made carpets.
        </p>
        <button className="about-read-more-btn" type="button" onClick={() => setIsAboutExpanded((prev) => !prev)}>
          {isAboutExpanded ? 'Read less' : 'Read more'}
        </button>
        {isAboutExpanded && (
          <>
            <p>
              Our machine-made collection showcases elegant Persian (Iranian), Turkish, Belgian, and contemporary European-inspired designs, blending timeless aesthetics with modern manufacturing techniques.
            </p>
            <p>
              Understanding that every project has unique requirements, we specialize in fully customized carpets and durries, offering bespoke sizes, colors, patterns, constructions, and materials to meet the exact specifications of our clients. Whether for luxury residences, hotels, hospitality projects, commercial spaces, retailers, wholesalers, or private labels, every product is crafted with meticulous attention to detail.
            </p>
            <p>
              At Orovia Bhadohi Carpets, we believe that a carpet is more than a floor covering—it is an expression of artistry, culture, and comfort. Our skilled artisans combine generations of expertise with carefully selected materials to create products that embody elegance, durability, and exceptional quality.
            </p>
            <p>
              Today, we proudly serve customers in domestic and international markets, building long-term partnerships through trust, craftsmanship, and consistent excellence. Every carpet we produce reflects our unwavering commitment to quality, innovation, and customer satisfaction.
            </p>
            <p>
              For over 55 years, our family's passion has been woven into every knot, every weave, and every design. At Orovia Bhadohi Carpets, we don't simply manufacture carpets—we create timeless pieces that transform spaces and preserve a legacy of Indian craftsmanship for generations to come.
            </p>
          </>
        )}
      </div>

    </section>
  );

  const craftsmanshipPointsSection = (
    <section className="section craftsmanship-section">
      <div className="craftsmanship-shell">
        <div className="craftsmanship-copy">
          <p className="eyebrow">Craftsmanship</p>
          <h2>Mastering tradition with meticulous attention.</h2>
          <p>
            From design development to final finishing, every stage of production is guided by restraint, quality, and precision.
          </p>
        </div>
        <div className="craftsmanship-cards">
          {craftsmanshipPoints.map((point, index) => (
            <div
              className={`craft-card scroll-reveal card-reveal ${index % 2 === 0 ? 'reveal-left' : 'reveal-right'}`}
              key={point.title}
            >
              <h3>{point.title}</h3>
              <p>{point.text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );

  return (
    <div className="patodia-shell">
      <header className="patodia-header">
        <div className="header-left">
          <button
            className="menu-toggle"
            type="button"
            aria-label="Open navigation menu"
            onClick={() => setIsMenuOpen((prev) => !prev)}
          >
            <span />
            <span />
            <span />
          </button>
        </div>

        <a className="brand-group brand-group-centered" href="#top">
          <img className="brand-logo" src={getAssetPath('/logo.png')} alt="Orovia Bhadohi Carpets logo" loading="eager" decoding="async" fetchPriority="high" />
          <div>
            <p className="brand-name">Orovia Bhadohi Carpets</p>
            <p className="brand-tag">Bhadohi • Luxury handmade carpets</p>
          </div>
        </a>

        <div className="header-right">
          <a
            className="contact-pill"
            href="#contact"
            onClick={(event) => {
              if (isMobile) {
                event.preventDefault();
                handleMobilePage('contact');
              }
            }}
          >
            Contact Us
          </a>
        </div>
      </header>

      <div className={`side-nav-backdrop ${isMenuOpen ? 'is-open' : ''}`} onClick={() => setIsMenuOpen(false)} />
      <aside className={`side-nav ${isMenuOpen ? 'is-open' : ''}`}>
        <button className="side-nav-close" type="button" aria-label="Close navigation menu" onClick={() => setIsMenuOpen(false)}>
          ×
        </button>
        <div className="side-nav-content">
          <p className="eyebrow">Navigate</p>
          {navigation.map((item) => (
            <a
              key={item.label}
              href={item.href}
              onClick={(event) => {
                if (isMobile && item.label === 'About') {
                  event.preventDefault();
                  handleMobilePage('about');
                  return;
                }

                if (isMobile && item.label === 'Home') {
                  event.preventDefault();
                  handleHomeView();
                  return;
                }

                setIsMenuOpen(false);
              }}
            >
              {item.label}
            </a>
          ))}
          <a
            className="side-nav-contact"
            href="#contact"
            onClick={(event) => {
              if (isMobile) {
                event.preventDefault();
                handleMobilePage('contact');
                return;
              }
              setIsMenuOpen(false);
            }}
          >
            Contact Us
          </a>
        </div>
      </aside>

      {isMobile && mobilePage === 'about' ? (
        <section className="mobile-page-section">
          <div className="mobile-page-card">
            <button className="mobile-page-back" type="button" onClick={handleHomeView}>
              ← Back
            </button>
            <p className="eyebrow">About</p>
            <h2>About Orovia Bhadohi Carpets</h2>
            <p>
              Orovia Bhadohi Carpets is a distinguished manufacturer of premium handmade and machine-made carpets, proudly based in Bhadohi, India—renowned worldwide as the Carpet City of India.
            </p>
            <p>
              Our family-led legacy has been shaped by generations of carpet-making expertise, and today we continue to create pieces that blend heritage craftsmanship with refined, contemporary design.
            </p>
            <img className="mobile-about-image" src={getAssetPath('/asset/Handloom/7.jpeg')} alt="Crafted carpet detail" loading="lazy" decoding="async" />
            <p>
              Our story began in 1980, when our family embarked on a journey dedicated to the art of carpet weaving. Today, as a second-generation family business, we continue to uphold this legacy with the same passion, integrity, and commitment to quality that has defined us for over five decades.
            </p>
            <div
              className="mobile-auto-carousel"
              aria-label="Featured carpet gallery"
              onTouchStart={handleCarouselTouchStart}
              onTouchEnd={handleCarouselTouchEnd}
            >
              <img
                key={mobileCarouselImages[activeCarouselIndex].src}
                className="mobile-auto-carousel-image"
                src={mobileCarouselImages[activeCarouselIndex].src}
                alt={mobileCarouselImages[activeCarouselIndex].alt}
                loading="lazy"
                decoding="async"
              />
              <div className="mobile-carousel-dots" aria-hidden="true">
                {mobileCarouselImages.map((_, index) => (
                  <span
                    key={`${mobileCarouselImages[index].src}-${index}`}
                    className={`mobile-carousel-dot ${index === activeCarouselIndex ? 'is-active' : ''}`}
                  />
                ))}
              </div>
            </div>
            <p>
              In 2026, Orovia Bhadohi Carpets embraced the digital era, expanding our reach to serve clients across the globe while remaining deeply connected to the craftsmanship that has been passed down through generations.
            </p>
            <p>
              We manufacture an extensive collection of handcrafted and machine-made floor coverings, including handmade wool carpets, cotton durries, handloom carpets, tencel carpets, viscose carpets, jute durries, hand-tufted carpets, and premium machine-made carpets.
            </p>
            <p>
              Our machine-made collection showcases elegant Persian (Iranian), Turkish, Belgian, and contemporary European-inspired designs, blending timeless aesthetics with modern manufacturing techniques.
            </p>
            <p>
              Understanding that every project has unique requirements, we specialize in fully customized carpets and durries, offering bespoke sizes, colors, patterns, constructions, and materials to meet the exact specifications of our clients. Whether for luxury residences, hotels, hospitality projects, commercial spaces, retailers, wholesalers, or private labels, every product is crafted with meticulous attention to detail.
            </p>
            <div className="mobile-about-image-pair">
              <img src={getAssetPath('/asset/Handloom/8.jpeg')} alt="Handloom detail 8" loading="lazy" decoding="async" />
              <img src={getAssetPath('/asset/Handloom/9.jpeg')} alt="Handloom detail 9" loading="lazy" decoding="async" />
            </div>
            <p>
              At Orovia Bhadohi Carpets, we believe that a carpet is more than a floor covering—it is an expression of artistry, culture, and comfort. Our skilled artisans combine generations of expertise with carefully selected materials to create products that embody elegance, durability, and exceptional quality.
            </p>
            <p>
              Today, we proudly serve customers in domestic and international markets, building long-term partnerships through trust, craftsmanship, and consistent excellence. Every carpet we produce reflects our unwavering commitment to quality, innovation, and customer satisfaction.
            </p>
            <p>
              For over 55 years, our family's passion has been woven into every knot, every weave, and every design. At Orovia Bhadohi Carpets, we don't simply manufacture carpets—we create timeless pieces that transform spaces and preserve a legacy of Indian craftsmanship for generations to come.
            </p>
          </div>
        </section>
      ) : isMobile && mobilePage === 'contact' ? (
        <section className="mobile-page-section">
          <div className="mobile-page-card">
            <button className="mobile-page-back" type="button" onClick={handleHomeView}>
              ← Back
            </button>
            <p className="eyebrow">Contact</p>
            <h2>Get in touch</h2>
            <div className="mobile-contact-details">
              <p>
                Orovia Bhadohi Carpets<br />
                Care of Mohiyuddin Ansari<br />
                Jamund, Bhadohi - 221401<br />
                Uttar Pradesh, India
              </p>
              <a href="tel:+917897777732">+91 78977 77732</a>
              <a href="tel:+917905675657">+91 79056 75657</a>
              <a href="mailto:info@oroviabhadohicarpets.com">info@oroviabhadohicarpets.com</a>
              <a href="mailto:sales@oroviabhadohicarpets.com">sales@oroviabhadohicarpets.com</a>
              <a href="https://oroviabhadohicarpets.com" target="_blank" rel="noreferrer">www.oroviabhadohicarpets.com</a>
            </div>
            <div
              className="mobile-auto-carousel"
              aria-label="Featured carpet gallery"
              onTouchStart={handleCarouselTouchStart}
              onTouchEnd={handleCarouselTouchEnd}
            >
              <img
                key={mobileCarouselImages[activeCarouselIndex].src}
                className="mobile-auto-carousel-image"
                src={mobileCarouselImages[activeCarouselIndex].src}
                alt={mobileCarouselImages[activeCarouselIndex].alt}
                loading="lazy"
                decoding="async"
              />
              <div className="mobile-carousel-dots" aria-hidden="true">
                {mobileCarouselImages.map((_, index) => (
                  <span
                    key={`${mobileCarouselImages[index].src}-${index}`}
                    className={`mobile-carousel-dot ${index === activeCarouselIndex ? 'is-active' : ''}`}
                  />
                ))}
              </div>
            </div>
          </div>
        </section>
      ) : (
      <main id="top" className="home-main">
        <section className="feature-image-section">
          <img src={getAssetPath('/asset/HandTufted Modern/2.jpeg')} alt="Hand tufted modern collection" loading="eager" decoding="async" fetchPriority="high" />
          <div className="feature-image-overlay">
            <div className="feature-image-copy">
              <p className="eyebrow">Hand crafted luxury carpets</p>
              <h1>Timeless handmade carpets for contemporary living.</h1>
              <p className="hero-text">
                With a legacy rooted in Bhadohi craftsmanship, Orovia creates bespoke rugs and carpets that blend heritage techniques with refined modern design.
              </p>
            </div>
          </div>
        </section>

        {isMobile ? craftsmanshipPointsSection : aboutSection}

        <section className="section craftsmanship-feature layered-feature">
          <div className="craftsmanship-feature-background">
            <img src={getAssetPath('/asset/Handloom/7.jpeg')} alt="Handloom background" loading="lazy" decoding="async" />
          </div>
          <div className="craftsmanship-feature-content">
            <div className="craftsmanship-feature-thumbs">
              <div className="feature-thumb scroll-reveal reveal-left">
                <img src={getAssetPath('/asset/Handloom/14.jpeg')} alt="Handloom detail 14" loading="lazy" decoding="async" />
              </div>
              <div className="feature-thumb scroll-reveal reveal-up">
                <img src={getAssetPath('/asset/Handloom/2.jpeg')} alt="Handloom detail 2" loading="lazy" decoding="async" />
              </div>
              <div className="feature-thumb scroll-reveal reveal-right">
                <img src={getAssetPath('/asset/Handloom/3.jpeg')} alt="Handloom detail 3" loading="lazy" decoding="async" />
              </div>
            </div>
            <div className="craftsmanship-feature-copy scroll-reveal reveal-up">
              <p className="eyebrow">Craftsmanship</p>
              <h2>Mastering tradition with meticulous attention.</h2>
              <p>
                From design development to final finishing, every stage of production is guided by restraint, quality, and precision.
              </p>
            </div>
          </div>
        </section>

        <section id="collections" className="section collection-section">
          <div className="section-heading">
            <p className="eyebrow">Featured collections</p>
            <h2>Signature rugs for refined living.</h2>
          </div>
          <div className="collection-grid">
            {collections.map((item, index) => (
              <article
                className={`collection-card scroll-reveal ${index % 2 === 0 ? 'reveal-left' : 'reveal-right'}`}
                key={item.title}
              >
                <img src={item.image} alt={item.title} loading="lazy" decoding="async" />
                <div className="collection-body">
                  <h3>{item.title}</h3>
                  <p>{item.description}</p>
                </div>
              </article>
            ))}
          </div>
        </section>

        {!isMobile && craftsmanshipPointsSection}

        {showcaseGroups.map((group) => (
          <section className="section gallery-section" key={group.title}>
            <div className="section-heading">
              <p className="eyebrow">Crafted in Bhadohi</p>
              <h2>{group.title}</h2>
              <p>{group.description}</p>
            </div>
            <div className="gallery-grid">
              {group.items.map((item, index) => (
                <a
                  className={`gallery-card scroll-reveal card-reveal ${index % 2 === 0 ? 'reveal-left' : 'reveal-right'}`}
                  key={item.title}
                  href={getImageRoute(item.slug)}
                >
                  <img src={item.image} alt={item.title} loading="lazy" decoding="async" />
                  <div className="gallery-caption">{item.title}</div>
                </a>
              ))}
            </div>
          </section>
        ))}


        <section id="contact" className="section contact-section">
          <div className="contact-copy">
            <p className="eyebrow">Get in touch</p>
            <h2>Experience the collection in person.</h2>
            <p>
              Orovia Bhadohi Carpets<br />
              Care of Mohiyuddin Ansari<br />
              Jamund, Bhadohi - 221401<br />
              Uttar Pradesh, India<br />
              Open for showroom visits, private consultations, and custom orders.
            </p>
          </div>
          <div className="contact-card">
            <h3>Contact details</h3>
            <p>Call or write for private appointments and bespoke projects.</p>
            <a href="tel:+917897777732">+91 78977 77732</a>
            <a href="tel:+917905675657">+91 79056 75657</a>
            <a href="mailto:info@oroviabhadohicarpets.com">info@oroviabhadohicarpets.com</a>
            <a href="mailto:sales@oroviabhadohicarpets.com">sales@oroviabhadohicarpets.com</a>
            <a href="https://oroviabhadohicarpets.com" target="_blank" rel="noreferrer">www.oroviabhadohicarpets.com</a>
          </div>
        </section>
      </main>
      )}

      <footer className="patodia-footer">
        <p>© 2026 Orovia Bhadohi Carpets. All rights reserved.</p>
      </footer>
    </div>
  );
}

export default App;
